// The landing FAQ — ONE source of truth read by THREE surfaces: the accordion in
// components/landing/LandingSections.tsx (plain text), the FAQPage JSON-LD the
// homepage emits (plain text), and /docs/faq (markdown). Because two of the
// three render these strings literally, they must stay free of markdown —
// backticks around a command showed up as backticks on the live page.
//
// VOICE — this is the one place on the site that answers questions rather than
// telling a story, so it is written plainly: normal product language, lowercase,
// no flourishes and no salesmanship. Say what the thing is, how it works, and
// what the person gets. If a sentence sounds like it was written to be admired,
// rewrite it.

export type Faq = { q: string; a: string }

export const FAQS: Faq[] = [
  {
    q: 'what is innernet?',
    a: 'innernet is a memory layer for ai tools. it stores the context of your work (decisions, preferences, technical details and the reasoning behind them) in one place, and any ai tool you connect can read and write it. instead of re-explaining your project at the start of every chat, the tool already has it. an agent called netti keeps it organised: it files new information into the right documents, updates them when something changes, and keeps a version history.',
  },
  {
    q: 'how do i start?',
    a: 'run npx innernet in your terminal. it signs you in through your browser, detects the ai tools installed on your machine, connects each one to your memory, and sets up the project you are in. tools it cannot detect (chatgpt, claude.ai, anything on another machine) connect by pasting innernet.live/api/mcp into their settings. setup takes about two minutes, and there is nothing left running afterwards.',
  },
  {
    q: 'which ai tools work today?',
    a: 'claude code, cursor, codex, windsurf, gemini cli, claude desktop, vs code, cline, roo, zed, continue, opencode and antigravity connect automatically. chatgpt and claude.ai connect through their own connector settings. any other tool that supports mcp can connect with the url, and there is a rest api for everything else.',
  },
  {
    q: 'what does it actually remember?',
    a: 'decisions and why you made them, preferences you have stated, constraints, approaches you tried and rejected, who is involved, and where the work currently stands. it is stored as documents and structured entries you can read and edit yourself, not as chat transcripts.',
  },
  {
    q: 'is my memory private?',
    a: 'yes. your data is isolated at the database level, so only your account can read it. api keys and tokens are stored hashed, sensitive fields can be encrypted at rest, and nothing is read until you connect a tool. one caveat worth stating plainly: once you connect an ai tool, that tool can read your memory, so connect the ones you trust.',
  },
  {
    q: 'what happens to my data if i leave?',
    a: 'you can export everything, or delete your account and all of its data, at any time from your settings.',
  },
  {
    q: 'do i have to change how i work?',
    a: 'no. you use the same tools in the same way. innernet records context as you work and your tools read it when they need it. the one new thing you can do is ask any connected tool what you decided about something, and get an answer from your own memory.',
  },
]

// The longer set for /docs/faq — the landing seven plus the questions people ask
// when comparing tools. Same rules: markdown-free, plain language, and every
// claim maps to something shipped.
export const DOCS_FAQS: Faq[] = [
  ...FAQS,
  {
    q: 'what is mcp, and why does innernet use it?',
    a: 'mcp (model context protocol) is an open standard that lets ai tools connect to outside services. claude, chatgpt, cursor and most current tools support it. innernet runs as a single mcp server, so one connection covers every tool instead of needing a separate integration for each one. connected tools get 35 tools for reading and writing your memory, and the same 35 are available as rest endpoints.',
  },
  {
    q: 'what is netti?',
    a: "netti is the agent that maintains your memory. it files new captures into the right documents, updates them when something changes (leaving a dated line in that document's history), and when you ask a question it assembles the relevant part of your memory rather than returning all of it. it runs on our servers, so you do not need to supply a model api key.",
  },
  {
    q: 'what is a context map?',
    a: 'each project is a context map: dimensions (markdown documents such as vision or technical), nodes (structured entries for people, decisions and artifacts), and a commit log of every change. you can branch a map to hold two directions at once and merge the one you keep.',
  },
  {
    q: 'how is innernet different from notion, obsidian, or a notes app?',
    a: 'a notes app stores what you write and leaves the organising to you. innernet maintains the structure itself: an agent files new information, keeps documents current and versions every change, and your ai tools read it directly instead of you pasting context in.',
  },
  {
    q: "how is innernet different from claude's or chatgpt's built-in memory?",
    a: 'built-in memory works only inside that one product, and you cannot export it, branch it, or let your other tools read it. innernet works across tools: the same memory is read by claude, chatgpt, cursor and any mcp client, and you can export or delete it whenever you want.',
  },
  {
    q: 'does innernet only work for code?',
    a: 'no. the structure of a context map is generated for each project, so a brand, a screenplay, a company or a research project each get a shape that fits. it works for creative and business work as well as engineering.',
  },
  {
    q: 'can i bring my existing chatgpt or claude history?',
    a: 'yes. npx innernet import reads an exported claude or chatgpt history and organises it into a project tree.',
  },
  {
    q: 'can i build my own product on innernet?',
    a: 'yes. the rest api and typescript sdk give you the same memory layer for your own apps and agents: a separate memory per end user, versioned context maps, and retrieval through a single /context call. a get request to /api/v1 returns a discovery document listing every endpoint, and api keys are created in your dashboard under connections → api.',
  },
]
